import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { announceAccountChange, useAuth } from "../auth";
import { AuthCard, FormError, errorMessage } from "../components/AuthCard";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { user, loading, setUser } = useAuth();
  const [state, setState] = useState<"verifying" | "verified" | "invalid">(token ? "verifying" : "invalid");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [resent, setResent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Tokens are single-use: make sure StrictMode's double effect run sends it once.
  const started = useRef(false);

  useEffect(() => {
    if (!token || loading || started.current) return;
    started.current = true;
    api
      .verifyEmail(token)
      .then((verified) => {
        setVerifiedEmail(verified.email);
        if (user && user.id === verified.id) setUser(verified);
        announceAccountChange();
        setState("verified");
      })
      .catch(() => setState("invalid"));
  }, [token, loading, user, setUser]);

  async function resend() {
    setError(null);
    try {
      setResent((await api.resendVerification()).message);
    } catch (err) {
      setError(errorMessage(err, "We couldn't send the email. Try again later."));
    }
  }

  if (state === "verifying") {
    return (
      <AuthCard title="Verifying your email">
        <p className="muted">One moment…</p>
      </AuthCard>
    );
  }

  if (state === "verified") {
    return (
      <AuthCard title="Email verified" subtitle={<>Thanks! {verifiedEmail} is confirmed.</>}>
        <Link to={user ? "/" : "/login"} className="btn btn-primary btn-block">
          {user ? "Continue to your courses" : "Sign in"}
        </Link>
      </AuthCard>
    );
  }

  const canResend = user && !user.email_verified;
  return (
    <AuthCard
      title="This link has expired"
      subtitle={
        user?.email_verified
          ? "Your email is already verified, so there's nothing more to do."
          : "Verification links work once and expire after a few days. Use the latest email we sent, or ask for a new one."
      }
      footer={<Link to={user ? "/" : "/login"}>{user ? "Back to your courses" : "Sign in"}</Link>}
    >
      {canResend &&
        (resent ? (
          <p className="notice" role="status">
            {resent}
          </p>
        ) : (
          <button className="btn btn-primary btn-block" onClick={() => void resend()}>
            Send a new verification link
          </button>
        ))}
      {!user && <p className="muted small">Sign in to send yourself a new verification link.</p>}
      <FormError message={error} />
    </AuthCard>
  );
}
